/**
 * Calendar utility functions for AMC insights path conversion charts
 * Contains date manipulation and validation logic
 */

/**
 * Format a date to ISO string format (YYYY-MM-DD)
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export function formatDateToISO(date) {
  if (!date) return null;
  return new Date(date).toISOString().split('T')[0];
}

/**
 * Check if a date is within a selected range
 * @param {Date} date - The date to check
 * @param {Object} changedDates - Object containing currentDay and endDay
 * @returns {boolean} True if date is selected
 */
export function isDateSelected(date, changedDates) {
  if (!changedDates.currentDay || !changedDates.endDay) {
    return false;
  }

  const dateString = formatDateToISO(date);
  const startDate = formatDateToISO(changedDates.currentDay);
  const endDate = formatDateToISO(changedDates.endDay);

  return dateString >= startDate && dateString <= endDate;
}

/**
 * Check if a date should be disabled based on various criteria
 * @param {Date} date - The date to check
 * @param {string} biweeklyDates - Biweekly date string
 * @param {string} lastWeekDates - Last week date string
 * @param {string} currentPath - Current application path
 * @returns {boolean} True if date should be disabled
 */
export function isDateDisabled(date, biweeklyDates, lastWeekDates, currentPath) {
  const dayOfWeek = date.getDay();
  const today = formatDateToISO(new Date());
  const dateString = formatDateToISO(date);
  const cutoffDate = '2024-06-30';

  // Disable if not Monday or Sunday
  if (dayOfWeek !== 1 && dayOfWeek !== 0) {
    return true;
  }

  // Disable if date is in the future
  if (today <= dateString) {
    return true;
  }

  // Disable if date is before cutoff
  if (dateString <= cutoffDate) {
    return true;
  }

  // Check weekly/biweekly availability
  return !checkWeeklybiweekly(date, biweeklyDates, lastWeekDates, currentPath);
}

/**
 * Check if a date is in the compare range
 * @param {Date} date - The date to check
 * @param {string} currentDay - Current day string
 * @param {string} endDay - End day string
 * @returns {boolean} True if date is in compare range
 */
export function isDateInCompareRange(date, currentDay, endDay) {
  if (!currentDay || !endDay) return false;

  const formattedDate = formatDateToISO(date);
  const start = new Date(currentDay);
  const end = new Date(endDay);

  const inBetweenMs = end.getTime() - start.getTime();
  const inBetweenDaysCount = Math.ceil(inBetweenMs / (1000 * 60 * 60 * 24));

  // Compare End: 1 day before current day
  const compareEndDate = new Date(new Date(currentDay).setDate(start.getDate() - 1));

  // Compare Start: inBetweenDaysCount days before compareEnd
  const compareStartDate = new Date(new Date(compareEndDate).setDate(compareEndDate.getDate() - inBetweenDaysCount));

  const compareStart = formatDateToISO(compareStartDate);
  const compareEnd = formatDateToISO(compareEndDate);

  // Build array of dates between compareStart and compareEnd
  const inBetweenDaysArray = [];
  let tempDate = new Date(compareStartDate);

  while (tempDate <= compareEndDate) {
    inBetweenDaysArray.push(formatDateToISO(tempDate));
    tempDate.setDate(tempDate.getDate() + 1);
  }

  return inBetweenDaysArray.includes(formattedDate);
}

/**
 * Check if a date is in range based on weekly/biweekly logic
 * @param {Date} date - The date to check
 * @param {string} BiweeklyDates - Biweekly date string
 * @param {string} LastWeekDates - Last week date string
 * @param {string} currentPath - Current application path
 * @returns {boolean} True if date is in range
 */
function checkWeeklybiweekly(date, BiweeklyDates, LastWeekDates, currentPath) {
  let biWeekarr = [];
  let startDate = new Date('2024-07-01');
  let endDate = new Date();
  let biweeklyEndDate = new Date(BiweeklyDates);
  let weeklyEndDate = new Date(LastWeekDates);

  const inputDate = new Date(date);
  const dayOfWeek = inputDate.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6

  // Enable only Mondays and Sundays for search-query-performance path
  if (
    currentPath &&
    (currentPath.includes('amazon/search-query-performance') ||
     currentPath === '/amazon/search-query-performance')
  ) {
    const input = new Date(date);
    const day = input.getDay(); // 0 = Sunday, 1 = Monday

    // Start date (fixed)
    const startDateLimit = new Date('2024-07-01');

    // Get last week's Monday
    const today = new Date();
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - today.getDay() + 1);

    const lastWeekMonday = new Date(currentMonday);
    lastWeekMonday.setDate(currentMonday.getDate() - 7);

    const lastWeekSunday = new Date(lastWeekMonday);
    lastWeekSunday.setDate(lastWeekMonday.getDate() + 6);

    // Allow only if it's a Monday or Sunday
    const isMonOrSun = day === 0 || day === 1;

    // And only if it's within the range
    const inRange = input >= startDateLimit && input <= lastWeekSunday;

    return isMonOrSun && inRange;
  }

  while (startDate <= endDate) {
    if (biweeklyEndDate <= weeklyEndDate) {
      biWeekarr.push(formatDateToISO(biweeklyEndDate));
      biWeekarr.push(
        formatDateToISO(
          new Date(biweeklyEndDate.getTime() - 24 * 60 * 60 * 1000)
        )
      );
      biweeklyEndDate.setDate(biweeklyEndDate.getDate() + 8);
      biWeekarr.push(formatDateToISO(weeklyEndDate));
    } else {
      biWeekarr.push(formatDateToISO(startDate));
      biWeekarr.push(
        formatDateToISO(
          new Date(startDate.getTime() - 24 * 60 * 60 * 1000)
        )
      );
      startDate.setDate(startDate.getDate() + 14);
    }
  }

  if (formatDateToISO(date) === formatDateToISO(new Date('2024-07-01'))) {
    return true;
  }

  let current7days = (() => {
    const today = new Date();

    const currentWeekMonday = new Date(today);
    currentWeekMonday.setDate(today.getDate() - today.getDay() + 1);

    const lastWeekMonday = new Date(currentWeekMonday);
    lastWeekMonday.setDate(currentWeekMonday.getDate() - 7);

    const lastWeekSunday = new Date(lastWeekMonday);
    lastWeekSunday.setDate(lastWeekMonday.getDate() + 6);

    const d = new Date(new Date(date).toDateString());

    if (new Date(date).getMonth() === new Date().getMonth()) {
      return (
        formatDateToISO(
          new Date(new Date(date).setDate(new Date(date).getDate() + 7))
        ) < formatDateToISO(new Date()) ||
        (d >= new Date(lastWeekMonday.toDateString()) &&
          d <= new Date(lastWeekSunday.toDateString()))
      );
    }

    return true;
  })();

  if (new Date(date).getMonth() === new Date().getMonth()) {
    return (
      biWeekarr.includes(
        formatDateToISO(
          new Date(new Date(date).setDate(new Date(date).getDate() + 1))
        )
      ) && current7days
    );
  }

  return biWeekarr.includes(
    formatDateToISO(
      new Date(new Date(date).setDate(new Date(date).getDate() + 1))
    )
  );
}

/**
 * Generate all calendar days for a given month
 * @param {Date} day - The day representing the month
 * @param {Object} options - Configuration options
 * @returns {Array} Array of day objects
 */
export function getCalendarDays(day, options) {
  const {
    biweeklyDates,
    lastWeekDates,
    currentPath,
    changedDates,
    compare,
    currentDay,
    endDay
  } = options;

  const firstDayOfMonth = new Date(day.getFullYear(), day.getMonth(), 1);
  const weekdayOfFirstDay = firstDayOfMonth.getDay();
  const calendarDays = [];

  // Add previous month days
  calendarDays.push(...generatePreviousMonthDays({
    firstDayOfMonth,
    weekdayOfFirstDay,
    biweeklyDates,
    lastWeekDates,
    currentPath,
    compare,
    currentDay,
    endDay,
    changedDates
  }));

  // Add current month days
  calendarDays.push(...generateCurrentMonthDays({
    firstDayOfMonth,
    biweeklyDates,
    lastWeekDates,
    currentPath,
    compare,
    currentDay,
    endDay,
    changedDates,
    day
  }));

  // Add next month days
  calendarDays.push(...generateNextMonthDays({
    firstDayOfMonth,
    biweeklyDates,
    lastWeekDates,
    currentPath,
    compare,
    currentDay,
    endDay,
    changedDates
  }));

  return calendarDays;
}

/**
 * Generate days from previous month
 * @param {Object} params - Parameters for generation
 * @returns {Array} Array of day objects
 */
function generatePreviousMonthDays({
  firstDayOfMonth,
  weekdayOfFirstDay,
  biweeklyDates,
  lastWeekDates,
  currentPath,
  compare,
  currentDay,
  endDay,
  changedDates
}) {
  const days = [];
  const weekdays = [1, 2, 3, 4, 5, 6, 0];
  const startIndex = weekdays.indexOf(weekdayOfFirstDay);

  for (let i = 0; i < startIndex; i++) {
    const previousDay = new Date(firstDayOfMonth);
    previousDay.setDate(firstDayOfMonth.getDate() - (startIndex - i));

    days.push(createDayObject({
      date: previousDay,
      currentMonth: false,
      changedDates,
      biweeklyDates,
      lastWeekDates,
      currentPath,
      compare,
      currentDay,
      endDay
    }));
  }

  return days;
}

/**
 * Generate days for current month
 * @param {Object} params - Parameters for generation
 * @returns {Array} Array of day objects
 */
function generateCurrentMonthDays({
  firstDayOfMonth,
  biweeklyDates,
  lastWeekDates,
  currentPath,
  compare,
  currentDay,
  endDay,
  changedDates,
  day
}) {
  const days = [];
  const daysInMonth = new Date(firstDayOfMonth.getFullYear(), firstDayOfMonth.getMonth() + 1, 0).getDate();
  const currentDate = new Date(firstDayOfMonth);

  for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
    if (dayIndex !== 0) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    days.push(createDayObject({
      date: new Date(currentDate),
      currentMonth: currentDate.getMonth() === day.getMonth(),
      changedDates,
      biweeklyDates,
      lastWeekDates,
      currentPath,
      compare,
      currentDay,
      endDay
    }));
  }

  return days;
}

/**
 * Generate days from next month
 * @param {Object} params - Parameters for generation
 * @returns {Array} Array of day objects
 */
function generateNextMonthDays({
  firstDayOfMonth,
  biweeklyDates,
  lastWeekDates,
  currentPath,
  compare,
  currentDay,
  endDay,
  changedDates
}) {
  const days = [];
  const lastDayOfMonth = new Date(firstDayOfMonth.getFullYear(), firstDayOfMonth.getMonth() + 1, 0);
  const weekdayOfLastDay = lastDayOfMonth.getDay();
  const remainingDays = 7 - weekdayOfLastDay;

  for (let i = 1; i <= remainingDays; i++) {
    const nextDay = new Date(lastDayOfMonth);
    nextDay.setDate(lastDayOfMonth.getDate() + i);

    days.push(createDayObject({
      date: nextDay,
      currentMonth: false,
      changedDates,
      biweeklyDates,
      lastWeekDates,
      currentPath,
      compare,
      currentDay,
      endDay
    }));
  }

  return days;
}

/**
 * Create a day object with all necessary properties
 * @param {Object} params - Parameters for day creation
 * @returns {Object} Day object
 */
function createDayObject({
  date,
  currentMonth,
  changedDates,
  biweeklyDates,
  lastWeekDates,
  currentPath,
  compare,
  currentDay,
  endDay
}) {
  return {
    currentMonth,
    date,
    month: date.getMonth(),
    number: date.getDate(),
    year: date.getFullYear(),
    selected: isDateSelected(date, changedDates),
    disabled: isDateDisabled(date, biweeklyDates, lastWeekDates, currentPath),
    compare: compare ? isDateInCompareRange(date, currentDay, endDay) : false
  };
}